<script lang="ts">
import { defineComponent } from 'vue'

/**
 * AppTextDisclosure
 *
 * Note: This component is not currently able to determine if the wrapped text
 * requires the disclosure toggle or not. If you have configured the component
 * to clamp at three lines and only two lines are visible, the disclosure text
 * will still appear.
 */
export default defineComponent({
  props: {
    /**
     * Maximum number of text lines to show when in the collapsed state.
     */
    maxLines: {
      type: Number,
      default: 2,
    },
    /**
     * Determines whether the component is in the expanded state, or not.
     */
    expanded: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines whether the component behaviour should be suppressed or not.
     * When supressed, text content will appear in full without the disclosure
     * UI. Use to dynamically apply disclosure based on external factors, such
     * as viewport size.
     */
    suppress: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['expand', 'collapse'],
  data() {
    return {
      isExpanded: this.expanded,
    }
  },
  computed: {
    buttonText() {
      return this.isExpanded ? 'Read less' : 'Read more'
    },
  },
  methods: {
    /**
     * Reveals any clipped text. Emits a `expand` event.
     */
    expand() {
      if (this.isExpanded) {
        return
      }
      this.isExpanded = true
      this.$emit('expand')
    },
    /**
     * Clips text to the configured number of lines. Emits a `collapse` event.
     */
    collapse() {
      if (!this.isExpanded) {
        return
      }
      this.isExpanded = false
      this.$emit('collapse')
    },
    /**
     * Toggles between the collapsed and expanded states. Emits a `expand` or
     * `collapse` event.
     */
    toggle() {
      if (!this.isExpanded) {
        this.expand()
      } else {
        this.collapse()
      }
    },
  },
})
</script>

<template>
  <div
    :class="{
      'line-clamp-[var(--linecount)]': !isExpanded && !suppress,
    }"
    :style="{ '--linecount': maxLines }"
  >
    <slot></slot>
  </div>
  <button v-if="!suppress" class="text-left text-sm underline" @click="toggle">
    {{ buttonText }}
  </button>
</template>
